import { ErrorIconRounded } from "@/components/common/Icons/ErrorIconRounded";
import { useWindowSize } from "client/hooks/utils/useWindowSize";
import { capitalize } from "client/utils/capitalize";
import { translateFunc } from "client/utils/translateFunc";
import { CompressingIcon } from "components/common/Icons/CompressingIcon";
import { DownloadingIcon } from "components/common/Icons/DownloadingIcon";
import { SettingsFillIcon } from "components/common/Icons/SettingsFillIcon";
import { UploadIcon } from "components/common/Icons/UploadIcon";
import { ToolTip } from "components/common/ToolTip/ToolTip";
import { Translate } from "components/common/Translate/Translate";
import { CompressImage } from "store/features/compress/types";

interface StatusProps {
    image: CompressImage;
    index: number;
    handleSettingsClick: (image: CompressImage) => void;
}

export const Status = ({ image, handleSettingsClick, index }: StatusProps) => {
    const { width } = useWindowSize();
    return (
        <div className="mb-auto flex items-center justify-center">
            {image.error ? (
                <div className="w-full h-full flex items-center justify-center">
                <ToolTip text={(translateFunc("corrupt-file-tip"))}>
                    <div className="flex flex-col items-center justify-center text-center p-4">
                        <ErrorIconRounded />

                        <div className="font-bold text-base mt-2"><Translate keyName="error"/></div>
                        <div className="text-gray-200 text-sm">
                            <span data-testid={`image-${index}-corrupt-text`}><Translate keyName="corrupt-file"/></span>
                        </div>
                    </div>
                </ToolTip>
            </div>
            ) : (
                <>
                    {image.state === "downloading" && image.downloading && (
                        <div
                            className="flex flex-col items-center"
                            data-testid={`preview-image-${index}-status-downloading-container`}
                        >
                            <DownloadingIcon width={30} height={30} />
                            <div className="capitalize" data-testid={`preview-image-${index}-status-downloading-text`}>
                                <Translate keyName="downloading" />
                            </div>
                        </div>
                    )}
                    {image.state === "uploading" && image.uploading && (
                        <div
                            className="flex flex-col items-center"
                            data-testid={`preview-image-${index}-status-uploading-container`}
                        >
                            <UploadIcon />
                            <div className="capitalize" data-testid={`preview-image-${index}-status-uploading-text`}>
                                <Translate keyName="uploading" />
                            </div>
                        </div>
                    )}
                    {image.state === "importing" && image.importing && (
                        <div
                            className="flex flex-col items-center"
                            data-testid={`preview-image-${index}-status-importing-container`}
                        >
                            <UploadIcon />
                            <div className="capitalize" data-testid={`preview-image-${index}-status-importing-text`}>
                                <Translate keyName="uploading" />
                            </div>
                        </div>
                    )}
                    {image.state === "compressing" && image.compressing && (
                        <div
                            className="flex flex-col items-center"
                            data-testid={`preview-image-${index}-status-compressing-container`}
                        >
                            <CompressingIcon />
                            <div className="capitalize" data-testid={`preview-image-${index}-status-compressing-text`}>
                                <Translate keyName="compressing" />
                            </div>
                        </div>
                    )}

                    {image.state === "compressed" && image.compressed && (
                        <div
                            className="flex items-center"
                            data-testid={`preview-image-${index}-status-compressed-container`}
                        >
                            <ToolTip
                                text={`${capitalize(translateFunc("new-size"))}: ${image.compressed.size}`}
                                inactive={width < 1024}
                            >
                                <div
                                    className="text-3xl font-bold"
                                    data-testid={`preview-image-${index}-status-compressed-text`}
                                >
                                    {image.compressed.quality > -1 ? "-" : "+"}
                                    {Math.abs(image.compressed.quality)}%
                                </div>
                            </ToolTip>
                            <div
                                className="group flex items-center"
                                onClick={() => {
                                    handleSettingsClick(image);
                                }}
                                data-testid={`preview-image-${index}-status-compressed-settings-icon-container`}
                            >
                                <ToolTip
                                    text={capitalize(translateFunc("adjust-quality"))}
                                    position={{
                                        y: "up",
                                    }}
                                >
                                    <div className="mt-2">
                                        <SettingsFillIcon hoverColor="var(--dark-blue)" />
                                    </div>
                                </ToolTip>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
